import type { OrgUnitSummary } from '@nadoutong/contracts';
import { Archive, Check, Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useArchiveOrgUnit, useCreateOrgUnit, useUpdateOrgUnit } from '../../api/useOrgs.ts';
import { localizeCaughtError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Chip, ConfirmDialog, FormField, Input } from '../../ui/index.ts';

/**
 * Org unit structure — the `structure` tab of `/organization`.
 *
 * Create and edit stay inline (the tab's established shape); what this
 * panel gained from console-web's routed `/team/units` page (epic #1
 * PR-4) is the management depth that was missing: a required-name check
 * that reports instead of silently disabling, inline failure surfaces
 * for all three mutations, success toasts, parent and status columns,
 * and — the one behaviour change — archive now always runs through a
 * danger `ConfirmDialog` instead of mutating on a single click.
 *
 * Both editors are real `<form>`s with a submit button, so Enter in the
 * name field submits as it does on the source page — a keyboard-only
 * operator must not need the mouse to reach the primary action. The
 * name field carries the source page's `maxLength` too, so an
 * over-long name is refused in the field rather than by a server 400.
 *
 * Ownership of the unit mutations lives here rather than in the page:
 * the page only needs to know which unit the operator is looking at.
 */

/** Mirrors console-web's `/team/units` form; the server rejects beyond it. */
const NAME_MAX_LENGTH = 120;
const CREATE_NAME_ID = 'organization-unit-create-name';
const CREATE_PARENT_ID = 'organization-unit-create-parent';
/** Shared by both row shapes so editing does not shift the layout. */
const ROW_CLASSES = 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-3 text-body';

interface Props {
  readonly orgId: string | undefined;
  readonly units: readonly OrgUnitSummary[];
  readonly canManageUnits: boolean;
  readonly onSelectUnit: (id: string | null) => void;
}

export function StructurePanel({ orgId, units, canManageUnits, onSelectUnit }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const createM = useCreateOrgUnit();
  const updateM = useUpdateOrgUnit();
  const archiveM = useArchiveOrgUnit();

  const [createName, setCreateName] = useState('');
  const [createParentId, setCreateParentId] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editParentId, setEditParentId] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<OrgUnitSummary | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const mutationPending = createM.isPending || updateM.isPending || archiveM.isPending;

  function cancelEdit(): void {
    setEditingUnitId(null);
    setEditName('');
    setEditParentId('');
    setEditError(null);
  }

  async function handleCreate(): Promise<void> {
    if (!canManageUnits || orgId === undefined) {
      return;
    }
    setCreateError(null);
    const name = createName.trim();
    if (name.length === 0) {
      setCreateError(t('organization.structure.name_required'));
      return;
    }
    try {
      const data = await createM.mutateAsync({
        orgId,
        body: { name, parentId: createParentId.length === 0 ? null : createParentId },
      });
      setCreateName('');
      setCreateParentId('');
      onSelectUnit(data.unit.id);
      toast.success(t('organization.structure.toast.created'));
    } catch (err) {
      setCreateError(localizeCaughtError(err, t, 'organization.structure.error.create'));
    }
  }

  async function handleSave(unit: OrgUnitSummary): Promise<void> {
    if (!canManageUnits || orgId === undefined) {
      return;
    }
    setEditError(null);
    const name = editName.trim();
    if (name.length === 0) {
      setEditError(t('organization.structure.name_required'));
      return;
    }
    try {
      const data = await updateM.mutateAsync({
        orgId,
        orgUnitId: unit.id,
        body: { name, parentId: editParentId.length === 0 ? null : editParentId },
      });
      cancelEdit();
      onSelectUnit(data.unit.id);
      toast.success(t('organization.structure.toast.saved'));
    } catch (err) {
      setEditError(localizeCaughtError(err, t, 'organization.structure.error.save'));
    }
  }

  async function handleArchive(): Promise<void> {
    if (!canManageUnits || orgId === undefined || archiveTarget === null) {
      return;
    }
    setArchiveError(null);
    try {
      await archiveM.mutateAsync({ orgId, orgUnitId: archiveTarget.id });
      onSelectUnit(null);
      setArchiveTarget(null);
      toast.success(t('organization.structure.toast.archived'));
    } catch (err) {
      setArchiveError(localizeCaughtError(err, t, 'organization.structure.error.archive'));
    }
  }

  return (
    <div className="rounded-lg border border-line bg-card p-4" data-testid="organization-structure">
      <header className="flex flex-col gap-1">
        <h3 className="text-body font-medium text-ink">{t('organization.structure.title')}</h3>
        <p className="text-caption text-ink-low">{t('organization.structure.subtitle')}</p>
      </header>
      {canManageUnits ? (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void handleCreate();
          }}
          className="mt-4 grid grid-cols-1 gap-3 rounded-lg border border-line bg-raised p-3 lg:grid-cols-[minmax(0,1fr)_minmax(12rem,18rem)_auto]"
        >
          <FormField
            label={t('organization.structure.name')}
            fieldId={CREATE_NAME_ID}
            required
            className="min-w-0"
            error={
              createError === null ? undefined : (
                <span data-testid="organization-unit-create-error">{createError}</span>
              )
            }
          >
            <Input
              id={CREATE_NAME_ID}
              data-testid={CREATE_NAME_ID}
              value={createName}
              maxLength={NAME_MAX_LENGTH}
              invalid={createError !== null}
              aria-describedby={createError === null ? undefined : `${CREATE_NAME_ID}-error`}
              onChange={(event) => {
                setCreateName(event.currentTarget.value);
                setCreateError(null);
              }}
              placeholder={t('organization.structure.name_placeholder')}
            />
          </FormField>
          <FormField
            label={t('organization.structure.parent')}
            fieldId={CREATE_PARENT_ID}
            className="min-w-0"
          >
            <select
              id={CREATE_PARENT_ID}
              value={createParentId}
              onChange={(event) => setCreateParentId(event.target.value)}
              className="rounded-md border border-line bg-card px-3 py-2 text-body text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40"
            >
              <option value="">{t('organization.structure.root_parent')}</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  - {unit.path}
                </option>
              ))}
            </select>
          </FormField>
          <button
            type="submit"
            data-testid="organization-unit-create-submit"
            disabled={mutationPending}
            title={t('organization.structure.create_title')}
            className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-md bg-forest px-4 text-body font-medium text-on-forest transition-colors hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            <span>{t('organization.action.create')}</span>
          </button>
        </form>
      ) : null}
      {units.length === 0 ? (
        <p className="mt-4 text-body text-ink-low">{t('organization.structure.empty')}</p>
      ) : (
        <div className="mt-4 divide-y divide-line-soft">
          {units.map((unit) => {
            const parent =
              unit.parentId === null
                ? null
                : (units.find((candidate) => candidate.id === unit.parentId) ?? null);
            const editing = editingUnitId === unit.id;
            const editNameId = `organization-unit-edit-name-${unit.id}`;
            const cells = (
              <>
                {editing ? (
                  <div className="flex min-w-0 flex-col gap-2">
                    <div className="grid min-w-0 grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1fr)_minmax(12rem,18rem)]">
                      <Input
                        id={editNameId}
                        data-testid={editNameId}
                        value={editName}
                        maxLength={NAME_MAX_LENGTH}
                        invalid={editError !== null}
                        aria-label={t('organization.structure.name')}
                        aria-describedby={editError === null ? undefined : `${editNameId}-error`}
                        onChange={(event) => {
                          setEditName(event.currentTarget.value);
                          setEditError(null);
                        }}
                      />
                      <select
                        value={editParentId}
                        aria-label={t('organization.structure.parent')}
                        onChange={(event) => setEditParentId(event.target.value)}
                        className="rounded-md border border-line bg-raised px-3 py-2 text-body text-ink focus:outline-none focus:ring-2 focus:ring-forest-bright/40"
                      >
                        <option value="">{t('organization.structure.root_parent')}</option>
                        {units
                          .filter((candidate) => candidate.id !== unit.id)
                          .map((candidate) => (
                            <option key={candidate.id} value={candidate.id}>
                              - {candidate.path}
                            </option>
                          ))}
                      </select>
                    </div>
                    {editError !== null ? (
                      <p
                        id={`${editNameId}-error`}
                        role="alert"
                        data-testid="organization-unit-edit-error"
                        className="m-0 text-caption text-bad"
                      >
                        {editError}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onSelectUnit(unit.id)}
                    className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(6rem,12rem)_4rem_6rem] items-center gap-4 text-left hover:text-forest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-ink">{unit.name}</span>
                      {unit.path !== unit.name ? (
                        <span className="block truncate font-mono text-caption text-ink-low">
                          {unit.path}
                        </span>
                      ) : null}
                    </span>
                    <span
                      data-testid={`organization-unit-parent-${unit.id}`}
                      className="min-w-0 truncate font-mono text-caption text-ink-mid"
                    >
                      {parent?.path ?? t('organization.structure.root_parent')}
                    </span>
                    <span className="text-right font-mono text-ink">{unit.memberCount}</span>
                    <span className="flex justify-end">
                      <Chip
                        tone={unit.status === 'active' ? 'ok' : 'neutral'}
                        lamp={false}
                        testId={`organization-unit-status-${unit.id}`}
                      >
                        {unit.status === 'active'
                          ? t('organization.structure.status.active')
                          : t('organization.structure.status.archived')}
                      </Chip>
                    </span>
                  </button>
                )}
                {canManageUnits ? (
                  <div className="flex shrink-0 items-center gap-1">
                    {editing ? (
                      <>
                        <button
                          type="submit"
                          data-testid={`organization-unit-edit-save-${unit.id}`}
                          disabled={mutationPending}
                          title={t('organization.action.save')}
                          aria-label={t('organization.structure.action.save', { unit: unit.path })}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-forest hover:bg-active disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                        >
                          <Check aria-hidden="true" className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={mutationPending}
                          onClick={cancelEdit}
                          title={t('organization.action.cancel')}
                          aria-label={t('organization.structure.action.cancel', {
                            unit: unit.path,
                          })}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-low hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                        >
                          <X aria-hidden="true" className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          data-testid={`organization-unit-edit-${unit.id}`}
                          disabled={mutationPending}
                          onClick={() => {
                            setEditingUnitId(unit.id);
                            setEditName(unit.name);
                            setEditParentId(unit.parentId ?? '');
                            setEditError(null);
                          }}
                          title={t('organization.action.edit')}
                          aria-label={t('organization.structure.action.edit', { unit: unit.path })}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-low hover:bg-active hover:text-forest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                        >
                          <Pencil aria-hidden="true" className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          data-testid={`organization-unit-archive-${unit.id}`}
                          disabled={mutationPending}
                          onClick={() => {
                            setArchiveError(null);
                            setArchiveTarget(unit);
                          }}
                          title={t('organization.action.archive')}
                          aria-label={t('organization.structure.action.archive', {
                            unit: unit.path,
                          })}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-low hover:bg-active hover:text-bad focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                        >
                          <Archive aria-hidden="true" className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
              </>
            );
            // The row becomes the edit form so the submit button sits
            // inside it and Enter in the name field saves; a static row
            // has nothing to submit and stays a plain container.
            return editing ? (
              <form
                key={unit.id}
                noValidate
                data-testid={`organization-unit-row-${unit.id}`}
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleSave(unit);
                }}
                className={ROW_CLASSES}
              >
                {cells}
              </form>
            ) : (
              <div
                key={unit.id}
                data-testid={`organization-unit-row-${unit.id}`}
                className={ROW_CLASSES}
              >
                {cells}
              </div>
            );
          })}
        </div>
      )}

      {archiveTarget !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !archiveM.isPending) {
              setArchiveTarget(null);
              setArchiveError(null);
            }
          }}
          tone="danger"
          title={t('organization.structure.archive_title')}
          body={
            <div className="flex flex-col gap-2">
              <p className="m-0">
                {t('organization.structure.archive_body', { unit: archiveTarget.path })}
              </p>
              {archiveError !== null ? (
                <p
                  role="alert"
                  data-testid="organization-unit-archive-error"
                  className="m-0 text-caption text-bad"
                >
                  {archiveError}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={t('organization.structure.archive_confirm')}
          cancelLabel={t('organization.action.cancel')}
          closeLabel={t('organization.structure.archive_close')}
          loading={archiveM.isPending}
          testId="organization-unit-archive-dialog"
          onConfirm={() => {
            void handleArchive();
          }}
        />
      ) : null}
    </div>
  );
}
